import { DataTable, Select, t, Text } from '@kanbios/ui';
import { FULL_HEIGHT } from '../../lib/tables';

export type PortAccess = 'public' | 'platform' | 'closed';
export type FirewallDecisions = Record<string, { proto: 'tcp' | 'udp'; port: number; access: PortAccess }[]>;
export interface Listening { proto: 'tcp' | 'udp'; port: number; addresses: string[]; processes: string[] }
export interface ReviewedServer { server: string; listening: Listening[] | null }

/** The Checkmk agent: always open to the platform only. */
const FIXED = new Set(['tcp/6556']);
/**
 * SSH: open to the platform (and the administrators' VPN) by default; open to
 * everyone on a server whose client connects to it directly (sponsor decision
 * of 21 October 2026). Never closed: the platform needs it.
 */
const SSH = 'tcp/22';

interface PortRow { key: string; server: string; proto: 'tcp' | 'udp'; port: number; processes: string[]; fixed: boolean; ssh: boolean }

const rowsOf = (servers: ReviewedServer[]): PortRow[] => servers.flatMap(s => (s.listening ?? []).map(l => ({
  key: `${s.server}/${l.proto}/${l.port}`, server: s.server, proto: l.proto, port: l.port, processes: l.processes,
  fixed: FIXED.has(`${l.proto}/${l.port}`), ssh: `${l.proto}/${l.port}` === SSH,
})));
const accessOf = (decisions: Record<string, PortAccess>, row: PortRow): PortAccess => {
  if (row.ssh) return decisions[row.key] === 'public' ? 'public' : 'platform';
  return decisions[row.key] ?? 'public';
};
/** SSH counts only when opened to everyone: kept to the platform, it is the default. */
const reviewed = (r: PortRow, decisions: Record<string, PortAccess>) => !r.fixed && (!r.ssh || accessOf(decisions, r) === 'public');

/** The firewall decisions sent with the enrolment: every reviewed port of every server. */
export function firewallFrom(servers: ReviewedServer[], decisions: Record<string, PortAccess>): FirewallDecisions {
  const rows = rowsOf(servers);
  return Object.fromEntries(servers.map(s => [s.server, rows
    .filter(r => r.server === s.server && reviewed(r, decisions))
    .map(r => ({ proto: r.proto, port: r.port, access: accessOf(decisions, r) }))]));
}

/** Count of ports per access, for the summary. */
export function portCounts(servers: ReviewedServer[], decisions: Record<string, PortAccess>) {
  const counts = { public: 0, platform: 0, closed: 0 };
  for (const r of rowsOf(servers)) if (reviewed(r, decisions)) counts[accessOf(decisions, r)]++;
  return counts;
}

/**
 * Ports review before enrolling (sponsor decision D4, 17 October 2026): the
 * firewall denies by default, so each port a server listens on is kept open to
 * everyone, to the platform only, or closed, by the person who enrols it. SSH
 * is kept to the platform unless opened to everyone, never closed.
 */
export function PortsReview({ servers, decisions, onChange, height }: {
  servers: ReviewedServer[];
  decisions: Record<string, PortAccess>;
  onChange: (key: string, access: PortAccess) => void;
  height?: number;
}) {
  const options = [
    { value: 'public', label: t('settings.enrolment.review.public') },
    { value: 'platform', label: t('settings.enrolment.review.platform') },
    { value: 'closed', label: t('settings.enrolment.review.closed') },
  ];
  const sshOptions = options.filter(o => o.value !== 'closed');
  const rows = rowsOf(servers);
  return (
    <DataTable
      label={t('settings.enrolment.review.table')}
      rows={rows}
      rowKey="key"
      toolbar={false}
      density="compact"
      height={height ?? FULL_HEIGHT}
      columns={[
        { key: 'server', label: t('settings.access.columns.server'), mono: true, width: 190, pinned: true },
        { key: 'port', label: t('settings.enrolment.review.port'), mono: true, width: 100, render: (r: PortRow) => `${r.port}/${r.proto}` },
        { key: 'processes', label: t('settings.enrolment.review.process'), grow: true, render: (r: PortRow) => r.processes.join(', ') || '—' },
        {
          key: 'access', label: t('settings.enrolment.review.access'), width: 220, editable: true, sortable: false,
          render: (r: PortRow) => (r.fixed
            ? <Text variant="body-sm" tone="mute">{t('settings.enrolment.review.fixed')}</Text>
            : (
              <Select
                size="sm"
                ariaLabel={t('settings.enrolment.review.accessOf', { port: `${r.port}/${r.proto}`, server: r.server })}
                options={r.ssh ? sshOptions : options}
                value={accessOf(decisions, r)}
                onChange={v => onChange(r.key, (v ?? 'public') as PortAccess)}
              />
            )),
        },
      ]}
      empty={<Text variant="body-sm" tone="mute">{t('settings.enrolment.review.none')}</Text>}
    />
  );
}
